import React, { useCallback, useMemo, useState } from 'react';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import AddIcon from '@material-ui/icons/Add';

import { IntlMsg } from '../../_core/IntlMsg/intl-msg.component';
import { useCompanySportTerminology } from '../../../lib/stores/company.store';
import {
  useAdHocContextValues,
  useComposeContextOptions
} from '../../../lib/services/communication.service';
import {
  CommunicationContextType,
  ComposeContextRowType,
  IComposeContextSelection
} from '../../../lib/types/communication.types';
import { CONTEXT_ROW_TYPES } from './context-selector.constants';
import { isValueKind } from './context-selector.helpers';
import { getMessages } from './context-selector.messages';
import { IContextSelectorProps } from './context-selector.types';
import { PreloadedContextRow } from './preloaded-context-row.component';
import { PaginatedContextRow } from './paginated-context-row.component';

export const ContextSelector: React.FC<IContextSelectorProps> = ({
  contexts,
  initialContexts,
  onChange,
  assessmentId,
  assessmentName,
  skipOptionQueries
}) => {
  const termLookup = useCompanySportTerminology();
  const messages = getMessages(termLookup);

  const {
    ageGroups,
    sessions,
    loading: optionsLoading,
    error: optionsError
  } = useComposeContextOptions(assessmentId, skipOptionQueries);
  const { levels, teams, loading: valuesLoading, error: valuesError } = useAdHocContextValues({
    assessmentId,
    skip: skipOptionQueries
  });
  // ONE consolidated row per kind, rendered in the propagation order the
  // registry encodes; each kind's selected entries become that row's chips.
  const presentTypes = useMemo(
    () => CONTEXT_ROW_TYPES.filter(
      (type) => contexts.some((row) => row.type === type)
    ),
    [contexts]
  );
  const selectionsByType = useMemo(() => {
    const grouped = {} as Record<ComposeContextRowType, IComposeContextSelection[]>;
    CONTEXT_ROW_TYPES.forEach((type) => {
      grouped[type] = contexts.filter(
        (row) => row.type === type && (isValueKind(type) ? row.value : row.id)
      );
    });
    return grouped;
  }, [contexts]);

  // Row lock state — pure UI, never in drafts. Seeded from the lag-free mount
  // contexts so pre-filled kinds open locked (live `contexts` trail reinitialize).
  const [readOnlyTypes, setReadOnlyTypes] = useState<ComposeContextRowType[]>(
    () => CONTEXT_ROW_TYPES.filter(
      (type) => initialContexts.some(
        (row) => row.type === type && (isValueKind(type) ? row.value : row.id)
      )
    )
  );

  // Per-kind pre-edit selections, captured on Edit so Cancel can revert the live
  // edits. Transient UI like the lock — never persisted into drafts.
  const [editSnapshots, setEditSnapshots] = useState<
    Partial<Record<ComposeContextRowType, IComposeContextSelection[]>>
  >({});

  const dropSnapshot = useCallback((type: ComposeContextRowType) => {
    setEditSnapshots((prev) => {
      if (!(type in prev)) return prev;
      const next = { ...prev };
      delete next[type];
      return next;
    });
  }, []);

  // An emptied row keeps a placeholder entry so it stays visible until removed.
  const handleSelectionsChange = useCallback((
    type: ComposeContextRowType,
    selections: IComposeContextSelection[]
  ) => {
    const others = contexts.filter((row) => row.type !== type);
    onChange(selections.length ? [...others, ...selections] : [...others, { type, label: '' }]);
  }, [contexts, onChange]);

  // Switching a row's kind drops its selections for one empty row of the new
  // kind — the same semantic as a single picker losing its pick.
  const handleRowTypeChange = useCallback((
    from: ComposeContextRowType,
    to: ComposeContextRowType
  ) => {
    if (from === to) return;
    dropSnapshot(from);
    setReadOnlyTypes((prev) => prev.filter((type) => type !== from));
    onChange([...contexts.filter((row) => row.type !== from), { type: to, label: '' }]);
  }, [contexts, onChange, dropSnapshot]);

  const handleRowRemove = useCallback((type: ComposeContextRowType) => {
    dropSnapshot(type);
    setReadOnlyTypes((prev) => prev.filter((existing) => existing !== type));
    onChange(contexts.filter((row) => row.type !== type));
  }, [contexts, onChange, dropSnapshot]);

  // Edit unlocks a row and snapshots its committed selections for a later Cancel.
  const handleEditRow = useCallback((type: ComposeContextRowType) => {
    setEditSnapshots((prev) => ({ ...prev, [type]: contexts.filter((row) => row.type === type) }));
    setReadOnlyTypes((prev) => prev.filter((existing) => existing !== type));
  }, [contexts]);

  // Approve keeps the live edits and re-locks the row.
  const handleApproveRow = useCallback((type: ComposeContextRowType) => {
    dropSnapshot(type);
    setReadOnlyTypes((prev) => [...prev.filter((existing) => existing !== type), type]);
  }, [dropSnapshot]);

  // Cancel reverts to the pre-edit snapshot and re-locks; a never-approved row
  // (no snapshot) has nothing to revert to, so it is dropped instead.
  const handleCancelRow = useCallback((type: ComposeContextRowType) => {
    const snapshot = editSnapshots[type];
    if (snapshot) {
      onChange([...contexts.filter((row) => row.type !== type), ...snapshot]);
      setReadOnlyTypes((prev) => [...prev.filter((existing) => existing !== type), type]);
    } else {
      onChange(contexts.filter((row) => row.type !== type));
      setReadOnlyTypes((prev) => prev.filter((existing) => existing !== type));
    }
    dropSnapshot(type);
  }, [editSnapshots, contexts, onChange, dropSnapshot]);

  const handleAddRow = useCallback(() => {
    const absent = CONTEXT_ROW_TYPES.find(
      (type) => !contexts.some((row) => row.type === type)
    );
    if (absent) onChange([...contexts, { type: absent, label: '' }]);
  }, [contexts, onChange]);

  // Not memoized: a cheap length compare, consumed only in JSX below.
  const allTypesPresent = presentTypes.length === CONTEXT_ROW_TYPES.length;

  return (
    <div>
      {/* The panel is assessment-scoped by design — the root context renders
          as a static, non-removable chip. */}
      <Chip
        size="small"
        label={<IntlMsg {...messages.assessmentChip} values={{ name: assessmentName || '—' }} />}
      />

      {presentTypes.map((type) => (
        type === CommunicationContextType.PLAYER_ASSESSMENT ? (
          <PaginatedContextRow
            key={type}
            type={type}
            selections={selectionsByType[type]}
            contexts={contexts}
            readOnly={readOnlyTypes.includes(type)}
            assessmentId={assessmentId}
            skipOptionQueries={skipOptionQueries}
            onSelectionsChange={handleSelectionsChange}
            onTypeChange={handleRowTypeChange}
            onEdit={handleEditRow}
            onApprove={handleApproveRow}
            onCancel={handleCancelRow}
            onRemove={handleRowRemove}
          />
        ) : (
          <PreloadedContextRow
            key={type}
            type={type}
            selections={selectionsByType[type]}
            contexts={contexts}
            readOnly={readOnlyTypes.includes(type)}
            ageGroups={ageGroups}
            sessions={sessions}
            levels={levels}
            teams={teams}
            optionsLoading={optionsLoading}
            valuesLoading={valuesLoading}
            optionsError={!!optionsError}
            valuesError={!!valuesError}
            onSelectionsChange={handleSelectionsChange}
            onTypeChange={handleRowTypeChange}
            onEdit={handleEditRow}
            onApprove={handleApproveRow}
            onCancel={handleCancelRow}
            onRemove={handleRowRemove}
          />
        )
      ))}

      <Box mt={1}>
        <Button
          size="small"
          color="primary"
          onClick={handleAddRow}
          startIcon={<AddIcon />}
          disabled={allTypesPresent}
        >
          <IntlMsg {...messages.addContext} />
        </Button>
      </Box>
    </div>
  );
};
